'use client';

import { type ReactNode } from 'react';
import { Lock } from 'lucide-react';
import { useSubscriptionStore } from '@/stores/subscriptionStore';
import { canAccessFeature, getUpgradeMessage, getMinimumTier } from '@/lib/tierEnforcement';
import type { SubscriptionTier } from '@/lib/tierEnforcement';
import { cn } from '@/lib/utils';

interface UpgradeGateProps {
  /** The feature key to check (e.g. 'advanced_analytics'). */
  feature: string;
  children: ReactNode;
  /** Optional custom message; auto-generated if omitted. */
  message?: string;
  className?: string;
}

/**
 * Conditionally renders children or a locked overlay based on the current
 * subscription tier.  In demo mode, children are always rendered.
 */
function UpgradeGate({ feature, children, message, className }: UpgradeGateProps) {
  const tier = useSubscriptionStore((s) => s.tier) as SubscriptionTier;
  const isDemoMode = useSubscriptionStore((s) => s.isDemoMode);

  // Demo mode — everything is unlocked
  if (isDemoMode) {
    return <>{children}</>;
  }

  const hasAccess = canAccessFeature(tier, feature);

  if (hasAccess) {
    return <>{children}</>;
  }

  // Build upgrade info
  const upgradeMsg = message ?? getUpgradeMessage(tier, feature);
  const minTier = getMinimumTier(feature);
  const tierLabel = minTier ? minTier.charAt(0).toUpperCase() + minTier.slice(1) : 'a higher';

  return (
    <div className={cn('relative', className)}>
      {/* Blurred content */}
      <div className="pointer-events-none select-none blur-sm opacity-50" aria-hidden>
        {children}
      </div>

      {/* Locked overlay */}
      <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/60 dark:bg-gray-900/70 backdrop-blur-[2px] rounded-lg z-10">
        <div className="flex flex-col items-center gap-3 max-w-xs text-center px-4">
          <div className="h-12 w-12 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
            <Lock className="h-6 w-6 text-gray-400 dark:text-gray-500" />
          </div>
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {upgradeMsg}
          </p>
          <a
            href="/billing"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            Upgrade to {tierLabel}
          </a>
        </div>
      </div>
    </div>
  );
}

export { UpgradeGate };
export type { UpgradeGateProps };
